import React, { PureComponent } from 'react';
import styled from "@emotion/styled";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ResponsiveContainer, Label} from 'recharts';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload

    return (
      <TooltipSurface>
        <TooltipHeader>
          <TooltipKicker>Month</TooltipKicker>
          <TooltipTitle>{data.sessionLabelFull}</TooltipTitle>
        </TooltipHeader>
        <TooltipRows>
          <TooltipRow>
            <TooltipLabel>Investigations Dismissed</TooltipLabel>
            <TooltipValue>{data.leadsDismissed}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Closed Renegotiations</TooltipLabel>
            <TooltipValue>{data.closedRenegotiations}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Closed Refinances</TooltipLabel>
            <TooltipValue>{data.closedRefinances}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Gross Profit</TooltipLabel>
            <TooltipValue>
              <TooltipAccent>$</TooltipAccent>
              {data.grossProfitNumber.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}
            </TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Gross Revenue Increase</TooltipLabel>
            <TooltipValue>
              {data.grossProfitPercent.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}
              <TooltipAccent>%</TooltipAccent>
            </TooltipValue>
          </TooltipRow>
        </TooltipRows>
      </TooltipSurface>
    );
  }

  return null;
};


export default class MemberMonthlyChart extends PureComponent {

  render() 
  {
    return (
      <ResponsiveContainer width="100%" height="100%" minHeight={320}>
        <BarChart
          data={this.props.selectedMemberMonthlyStats}
          layout="horizontal"
          margin={{
            top: 8,
            right: 18,
            left: 18,
            bottom: 6,
          }}
        >
          <XAxis dataKey="sessionLabel" tick={{fontSize: 12, fill: "#666C78"}} tickLine={{stroke: "#BDBFC4"}} axisLine={{stroke: "#D4D6DB"}}>
            <Label angle={0} position='left' style={{ textAnchor: 'right'}} offset={-25} dy={20}>
              Year/Month
            </Label>
          </XAxis>
          <YAxis 
            type="number" 
            yAxisId="left" 
            orientation="left" 
            stroke="#5a5c68" 
            tick={{fontSize: 12, fill: "#666C78"}}
            tickLine={{stroke: "#BDBFC4"}}
            axisLine={{stroke: "#D4D6DB"}}
            domain={[0, dataMax => dataMax === 0 ? 1 : (Math.round(((dataMax * 1.1))/1000)*1000)]}
            tickFormatter={tick => {
              return `$${tick.toLocaleString('en-US')}`
            }}
          >
            <Label angle={270} position='right' style={{ textAnchor: 'middle' }} offset={-70}>
              Gross Profit
            </Label>
          </YAxis>
          <YAxis dataKey="totalCompleted" type="number" domain={[0, dataMax => dataMax === 0 ? 1 : (dataMax * 2)]} yAxisId="right" orientation="right" stroke="#5a5c68" tick={{fontSize: 12, fill: "#666C78"}} tickLine={{stroke: "#BDBFC4"}} axisLine={{stroke: "#D4D6DB"}}>
            <Label angle={90} position='right' style={{ textAnchor: 'middle' }} offset={-20}>
              Closed Investigations
            </Label>
          </YAxis>
          <Tooltip content={<CustomTooltip/>} cursor={{fill: `rgba(147, 149, 153, 0.12)`}} />
          <Legend wrapperStyle={{fontSize: "0.78rem", fontWeight: 700, color: "#666C78", paddingTop: "0.35rem"}} />
          <ReferenceLine y={0} stroke="#4b4b4d" yAxisId="left" />
          <Bar yAxisId="left" dataKey="grossProfitNumber" fill="#80C486" stackId="a" name="Gross Profit" />
          <Bar yAxisId="right" dataKey="closedRenegotiations" fill="#6e88cc" stackId="b" name="Renegotiations" />
          <Bar yAxisId="right" dataKey="closedRefinances" fill="#4CA855" stackId="c" name="Refinances" />
          <Bar yAxisId="right" dataKey="leadsDismissed" fill="#B486BF" stackId="d" name="Dismissed Investigations" />
        </BarChart>
      </ResponsiveContainer>
    );
  }
}

const TooltipSurface = styled.div(({ theme }) => `
  width: 17rem;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.75rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.85rem 1.9rem rgba(15, 23, 42, 0.16);
`)

const TooltipHeader = styled.div(({ theme }) => `
  padding: 0.75rem 0.85rem 0.65rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const TooltipKicker = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const TooltipTitle = styled.div(({ theme }) => `
  margin-top: 0.15rem;
  color: ${theme.palette.common.black};
  font-size: 0.95rem;
  font-weight: 850;
  line-height: 1.25;
`)

const TooltipRows = styled.div(`
  display: grid;
  gap: 0;
  padding: 0.45rem 0.85rem 0.65rem;
`)

const TooltipRow = styled.div(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.8rem;
  align-items: baseline;
  padding: 0.38rem 0;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorderLight};

  &:last-of-type {
    border-bottom: 0;
  }
`)

const TooltipLabel = styled.span(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.76rem;
  font-weight: 750;
  line-height: 1.2;
`)

const TooltipValue = styled.span(({ theme }) => `
  display: inline-flex;
  align-items: baseline;
  gap: 0.12rem;
  color: ${theme.palette.common.black};
  font-size: 0.82rem;
  font-weight: 850;
  line-height: 1.2;
  text-align: right;
`)

const TooltipAccent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-weight: 850;
`)
